import { useContext, useEffect, useMemo, useRef } from 'react';
import { FrameworkContext } from '../../cnr/contexts/FrameworkContent';
import { PageDimmerContext } from '../../cnr/contexts/PageDimmerContext';
import { ParentContext } from '../../cnr/contexts/ParentContext';
import { uniqueKey } from '../../common/keys';
import { gEnums } from '../../enums/globalEnums';
import { _animateTypes } from '../../motions/AnimateComponent';
import MotionComponent from '../../motions/MotionComponent';
import { DesktopContext } from '../../wrappers/DesktopWrapper';
import GridItemsSort from './GridItemsSort';
import IconGrid, { permissionGroupTypes } from './IconsGrid';
import { gridIconElements } from './gridItems/SegIconGroup';
import { useStyles } from 'global/cnr/contexts/useHelpers/useStyles';
import usePageFunctions from 'global/cnr/contexts/useHelpers/usePageFunctions';

/**
 * 
 * @param {object} props (pageIcons) 
 * @returns an element containing a group of gridIcons. Each gridIcon will redirect the app to a new page
 */
const IconGrids = (props) => {

  const { pageIcons } = props

  const { setStyleAndClass } = useStyles()
  const page_fns = usePageFunctions()

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}

  const { appSettings_state, currentSettings_state, page_state, storage_state, transition_state } = states
  const { appStorageUrlFiles } = storage_state ?? {}
  const { image: image_global } = appStorageUrlFiles ?? {}

  // transitionContext  
  const { transitions } = transition_state ?? {}
  const transition = transitions ? transitions[_animateTypes.landing] : null
  const { showTransition } = transition ?? {}

  // appSettingsContext 
  const { propOverrideOn } = appSettings_state

  // desktopContext
  const desktopContext = useContext(DesktopContext);
  const { desktop_state } = desktopContext ?? {}
  const { forDesktop } = desktop_state ?? {}

  // frameworkContextpageItemsShown 
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}
  const { permissionGroups } = framework_state ?? {}

  // pageDimmerContext
  const pageDimmerContext = useContext(PageDimmerContext)
  const { pageDimmer_handlers } = pageDimmerContext ?? {}

  const { aps_global, aps_viewItems } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page } = _.get(page_state, 'pageSettings', {});

  // pageContext  
  const { syncViewItems } = page_fns ?? {}

  const backgroundSaC = setStyleAndClass({ item: gEnums.projectStyles.background })
  const { style } = backgroundSaC ?? {}
  const { bgi } = style ?? {}

  const { appVoting } = aps_global ?? {}
  const { themedItems } = aps_global ?? {}
  const { background } = themedItems ?? {}
  const { useBackgroundImage } = background ?? {}

  const { allowVoting, showVotingManagerOnHomeScreen, showVotingOnHomeScreen } = appVoting ?? {}
  const { gridIcons } = aps_global && aps_global.themedItems ? aps_global.themedItems : {}
  const { iconsPerRow: iconsPerRow_gi } = gridIcons ?? {}

  let _backgroundImage;
  if (useBackgroundImage && image_global && bgi && image_global[bgi] && image_global[bgi].urls && image_global[bgi].urls.full) {
    _backgroundImage = image_global[bgi].urls.full
    backgroundSaC.style.backgroundImage = `url(${_backgroundImage})`
    delete backgroundSaC.style.bgi
  }

  // eslint-disable-next-line 
  const globalIconsPerRow = iconsPerRow_gi ? iconsPerRow_gi : 3

  const { layout: layout_page } = aps_page ?? {}
  const { iconsPerRow } = layout_page ?? {}

  let _iconsPerRow = iconsPerRow || globalIconsPerRow

  const segProps = {}

  const defaultSnC = setStyleAndClass({ item: gEnums.projectStyles.gridIcons })

  if (syncViewItems) { syncViewItems(aps_global, aps_viewItems, aps_page) }

  let iconCount = permissionGroups && permissionGroups.page ? Object.keys(permissionGroups.page).length : 0

  if (!pageIcons && allowVoting && (showVotingManagerOnHomeScreen || showVotingOnHomeScreen)) { iconCount++ }

  if (iconCount <= _iconsPerRow) { _iconsPerRow = iconCount }

  const itemRef = useRef()
  const { current: currentRef } = itemRef ?? {}

  const cn_gic = 'grid-icons-container ig'
  let cn_gicn = defaultSnC?.className

  if (forDesktop) { cn_gicn += ' dt' }

  useEffect(() => {
    pageDimmer_handlers && pageDimmer_handlers.handleKill_pageDimmer()
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [currentRef]);

  const gridContainer = (contents, appUserContents, appAdminContents, ticketingContents, registrationContents, adminContents, hiddenContents) => {
    return <div key={uniqueKey('iggc')} {...backgroundSaC}>
      <div ref={itemRef}></div>
      <div className={cn_gic} {...segProps}>
        <div className={cn_gicn}>
          {contents}
          {appUserContents}
          {appAdminContents}
          {ticketingContents}
          {registrationContents}
          {adminContents}
          {hiddenContents}
        </div>
      </div>
    </div>
  }; // memoize gridContainer with necessary dependencies

  const content = useMemo(() => {
    const { ready, appUser } = permissionGroups ?? {}
    if (ready) {
      return gridContainer(
        <IconGrid permissionGroupType={permissionGroupTypes.page} />,
        appUser.length > 0 && <IconGrid permissionGroupType={permissionGroupTypes.appUser} />,
        // appAdmin.length > 0 && <IconGrid permissionGroupType={permissionGroupTypes.appAdmin} />,
        // ticketing.length > 0 && <IconGrid permissionGroupType={permissionGroupTypes.ticketing} />,
        // admin.length > 0 && <IconGrid permissionGroupType={permissionGroupTypes.admin} />,
        // pageHidden.length > 0 && <IconGrid permissionGroupType={permissionGroupTypes.pageHidden} />,
      )
    } else {
      return <div></div>
    }
  }, [permissionGroups]);

  const gridContent = () => {
    if (showTransition) {
      return <MotionComponent transition={transition}>
        {content}
      </MotionComponent>
    } else {
      return content
    }
  };

  if (propOverrideOn) {
    const gridItems = gridIconElements({ gridItemGroup: permissionGroups.page, textLimit: 14 })
    return <GridItemsSort
      gridItems={gridItems}
      gridItemGroup={permissionGroups.page}
      defaultSnC={cn_gicn}
      backgroundSaC={backgroundSaC}
      iconsPerRow={_iconsPerRow}
    />
  } else {
    return gridContent()
  }
}

export default IconGrids